import React from 'react';
import { Alert, Typography } from '@mui/material';

// What a queue is allowed to say about itself.
//
// Three different states read the same way if nobody separates them: nothing has loaded yet, the
// feed is down, and there is genuinely nothing waiting. On a gate board the third is an all-clear
// somebody acts on - they stop looking for the truck, they sign the handover - so the first two must
// never be printed in its words.
//
// A populated list has the same problem in reverse. Rows that arrived four minutes ago look exactly
// like rows that arrived four seconds ago, so a desk reading a stale board calls in a truck that has
// already gone and misses the one that has just arrived. Where the feed has stopped answering, the
// rows stay - they are the last thing known - but they are labelled as what they are.

/** How old the last good read is, in words a desk uses. */
export const freshness = (updatedAt) => {
  const age = Date.now() - Date.parse(updatedAt || '');
  if (!Number.isFinite(age)) return '';
  const minutes = Math.floor(age / 60000);
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  return hours === 1 ? 'an hour ago' : `${hours} hours ago`;
};

/**
 * The line a queue shows when it has no rows: loading, unavailable, or a real all-clear.
 * `empty` is the all-clear, and is only ever used when the feed has actually answered.
 */
export const EmptyState = ({ updatedAt, unavailable, empty, subject = 'This list' }) => {
  if (unavailable) return <Alert severity="warning" sx={{ mt: 1 }}>{`${subject} is unavailable, so nothing here can be relied on. Retrying automatically.`}</Alert>;
  if (!updatedAt) return <Typography color="text.secondary" sx={{ mt: 1 }}>Loading…</Typography>;
  return <Typography color="text.secondary" sx={{ mt: 1 }}>{empty}</Typography>;
};

/**
 * The line a queue shows above rows it is no longer sure about. Nothing at all while the feed is
 * answering, because a board that cries stale every poll is a board nobody reads.
 */
export const StaleNotice = ({ updatedAt, unavailable }) => {
  if (!unavailable) return null;
  const when = freshness(updatedAt);
  return (
    <Alert severity="warning" sx={{ mb: 1 }}>
      {when ? `Showing what was last read ${when}; the feed is not answering.` : 'The feed is not answering, so this may be out of date.'}
    </Alert>
  );
};

export default EmptyState;
